<template>
  <div class="text-left">
    <BreadcrumbsNav />

    <!-- Hero -->
    <v-row align="center" class="mb-8">
      <v-col cols="12" md="8">
        <h1 class="text-h4 font-weight-black mb-4">
          L'espace des enfants
          <br />
          <span class="primary--text">& des familles</span>
        </h1>
        <p class="text-subtitle-1 mb-4">
          Donnez la parole aux enfants ! En 2 clics sur une tablette, ils évaluent le repas du jour. Avec Labrigame, ils
          apprennent à moins gaspiller en s'amusant. Les familles restent informées de ce qui se passe à la cantine.
        </p>
        <div class="d-flex flex-wrap mb-4" style="gap: 8px">
          <v-chip color="primary" small outlined>Vote enfants tablette</v-chip>
          <v-chip color="primary" small outlined>Sensibilisation anti-gaspillage</v-chip>
          <v-chip color="green darken-1" dark small>Labrigame disponible</v-chip>
        </div>
        <div class="d-flex flex-wrap" style="gap: 12px">
          <v-btn color="primary" large :to="{ name: 'ContactPage' }">Voir la démo</v-btn>
        </div>
      </v-col>
      <v-col cols="12" md="4">
        <v-img
          src="/static/images/doodles-dsfr/secondary/DogDoodle.png"
          contain
          max-width="100%"
          class="mx-auto rounded-0 labricot-img"
        />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 1 — Vote enfants (feature phare) -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6" order-md="1" order="2">
        <v-chip color="primary" outlined small class="mb-3">Élèves · Maternelle & Primaire</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">
          <v-icon color="primary" class="mr-1">$star-fill</v-icon>
          En 2 clics, les enfants donnent leur avis sur le repas
        </h2>
        <p>
          Sur une borne ou une tablette en fin de repas, chaque enfant répond à deux questions simples avec des
          pictogrammes accessibles, sans avoir besoin de lire.
        </p>
        <div class="my-4">
          <v-card outlined class="pa-3 mb-3 d-inline-flex align-center" style="gap: 8px">
            <span class="text-h5">😄</span>
            <span class="text-h5">😐</span>
            <span class="text-h5">😞</span>
            <span class="ml-2 font-weight-medium">As-tu aimé le repas ?</span>
          </v-card>
          <br />
          <v-card outlined class="pa-3 d-inline-flex align-center" style="gap: 8px; margin-top: 4px">
            <span class="text-caption grey--text">Si non : Pas assez · Je n'ai pas aimé · Autre raison</span>
          </v-card>
        </div>
        <ul class="mt-2 mb-4">
          <li>Résultats agrégés visibles en temps réel par l'animateur</li>
          <li>Retours automatiquement transmis à la cuisine centrale</li>
          <li>Aucune saisie papier, aucun intermédiaire</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : moins de plats peu appréciés servis à répétition, moins de gaspillage
        </v-chip>
      </v-col>
      <v-col cols="12" md="6" order-md="2" order="1">
        <!-- TODO: remplacer src par la capture de la tablette vote enfants ("As-tu aimé le repas ?") -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 2 — Visualisation ludique du gaspillage -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6">
        <!-- TODO: remplacer src par la capture "Montrer aux enfants" (visualisation ludique gaspillage) -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
      <v-col cols="12" md="6">
        <v-chip color="primary" outlined small class="mb-3">Animateur · Table de convives</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Rendre visible le gaspillage pour les enfants</h2>
        <p>
          Les enfants savent qu'il y a du gaspillage, mais ne réalisent pas les quantités astronomiques jetées chaque
          jour. Labricot propose des outils ludiques pour matérialiser, avec eux, ce qui est gaspillé à chaque repas.
        </p>
        <ul class="mt-2 mb-4">
          <li>Visualisation du gaspillage par table, par repas</li>
          <li>Équivalences concrètes accessibles aux enfants</li>
          <li>Petits défis collectifs de réduction du gaspillage</li>
          <li>Les enfants deviennent acteurs de la démarche anti-gaspi</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : prise de conscience et implication des enfants dès la maternelle
        </v-chip>
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 3 — Labrigame (en déploiement) -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6" order-md="1" order="2">
        <div class="d-flex align-center mb-3" style="gap: 8px">
          <v-chip color="primary" outlined small>Collégiens · Lycéens · Animateurs</v-chip>
          <v-chip color="green darken-1" dark small>
            <v-icon left x-small>$check-line</v-icon>
            Disponible
          </v-chip>
        </div>
        <h2 class="text-h5 font-weight-bold mb-4">Labrigame — L'apprentissage gamifié anti-gaspillage</h2>
        <p>
          Labrigame transforme l'éducation alimentaire en aventure collective. Les convives répondent à des enquêtes,
          relèvent des challenges terrain et progressent dans un parcours pédagogique conçu par des cuisiniers,
          diététiciens et éducateurs.
        </p>
        <ul class="mt-2 mb-4">
          <li>Enquêtes et sondages associés aux actions nutrition/santé</li>
          <li>Challenges anti-gaspillage collectifs ("Battle Resto")</li>
          <li>Missions terrain et défis hebdomadaires</li>
          <li>Badges, classements et progression ludique</li>
          <li>Contenu pédagogique capitalisable d'une année à l'autre</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : engagement des convives, réduction du gaspillage par intelligence collective
        </v-chip>
      </v-col>
      <v-col cols="12" md="6" order-md="2" order="1">
        <!-- TODO: remplacer src par la capture Labrigame (enquêtes / challenges / classement) -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 4 — Familles & Parents -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6">
        <v-img
          src="/static/images/sitting-person-color.png"
          contain
          max-width="100%"
          class="mx-auto rounded labricot-img"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-chip color="primary" outlined small class="mb-3">Parents · Familles</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Les familles restent informées</h2>
        <p>
          Consultez les informations de la cantine, les statistiques de satisfaction hebdomadaires et les initiatives
          anti-gaspillage de l'établissement. Votre enfant est acteur — suivez sa progression.
        </p>
        <ul class="mt-2 mb-4">
          <li>Accès aux informations du restaurant de votre enfant</li>
          <li>Statistiques de satisfaction et gaspillage de la semaine</li>
          <li>Communication des initiatives de l'établissement</li>
        </ul>
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- CTA final -->
    <v-row justify="center" class="my-12 text-center">
      <v-col cols="12" md="8">
        <h2 class="text-h5 font-weight-bold mb-4">Impliquez les enfants dans la démarche anti-gaspillage</h2>
        <p class="mb-6">
          Le vote en 2 clics, la visualisation ludique du gaspillage et Labrigame — des outils simples pour une
          restauration collective plus responsable.
        </p>
        <div class="d-flex flex-wrap justify-center" style="gap: 12px">
          <v-btn color="primary" large :to="{ name: 'ContactPage' }">Voir la démo</v-btn>
          <v-btn color="primary" large outlined href="/creer-mon-compte">Créer mon compte</v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BreadcrumbsNav from "@/components/BreadcrumbsNav"

export default {
  name: "ConvivePage",
  components: { BreadcrumbsNav },
}
</script>

<style scoped>
.labricot-img {
  max-height: 400px;
  width: auto;
}
.placeholder-img {
  min-height: 220px;
  background-color: #f5f5f5;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
}
@media (max-width: 600px) {
  .labricot-img {
    max-width: 320px;
    height: auto;
    max-height: none;
  }
}
</style>
